import type { SupportMarkReadResponse, SupportMessageApi, SupportThreadRow } from "@redipos/contracts";
import { http, getToken } from "../../shared/http.js";
import { API_BASE } from "../../constants.js";

export const soporteApi = {
  threads: () => http.get<SupportThreadRow[]>("/support/threads"),
  list: (tenantId?: string | null) => {
    const q = tenantId ? `?targetTenantId=${encodeURIComponent(tenantId)}` : "";
    return http.get<SupportMessageApi[]>(`/support${q}`);
  },
  send: (tenantId: string | null, content: string) =>
    http.post<SupportMessageApi>("/support", { targetTenantId: tenantId, content }),
  markRead: (tenantId: string | null) =>
    http.post<SupportMarkReadResponse>("/support/read", { targetTenantId: tenantId }),
  /** Notifica al cliente de un tenant que el admin está escribiendo. Fire-and-forget. */
  notifyTyping: (tenantId: string) => {
    return fetch(`${API_BASE}/support/typing`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      },
      body: JSON.stringify({ targetTenantId: tenantId }),
    }).catch(() => null);
  },
  /**
   * Abre un stream SSE. Devuelve una función de cancelación.
   * Usamos `fetch` + `ReadableStream` en vez de `EventSource` porque el ERP
   * autentica con `Authorization: Bearer <erp_token>` y `EventSource` no
   * permite setear headers. El parser SSE maneja reconexión manual.
   */
  connectStream: (
    onMessage: (msg: SupportMessageApi) => void,
    onTyping: (data: { from: { id: string; usuario: string; rol: string }; tenantId: string }) => void,
    onConnect: () => void,
    onRead?: (payload: { tenantId?: string }) => void,
    onDisconnect?: () => void,
  ): (() => void) => {
    if (typeof window === "undefined" || typeof fetch === "undefined") {
      return () => { /* noop en SSR / test */ };
    }

    let cancelled = false;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const decoder = new TextDecoder();
    let buffer = "";

    const dispatch = (rawEvent: string, dataStr: string) => {
      const event = rawEvent || "message";
      try {
        const data = dataStr ? JSON.parse(dataStr) : null;
        if (event === "connected") {
          try { onConnect(); } catch { /* noop */ }
        } else if (event === "message") {
          const message = (data && (data as { message?: SupportMessageApi }).message) ?? (data as SupportMessageApi);
          if (message && message.id) onMessage(message);
        } else if (event === "typing") {
          if (data) onTyping(data as { from: { id: string; usuario: string; rol: string }; tenantId: string });
        } else if (event === "read") {
          const payload = (data && (data as { message?: { tenantId?: string } }).message) ?? data;
          try { onRead?.((payload ?? {}) as { tenantId?: string }); } catch { /* noop */ }
        }
      } catch {
        /* payload mal formado: ignorar */
      }
    };

    const parseChunk = (chunk: string) => {
      buffer += chunk;
      let idx;
      // eslint-disable-next-line no-cond-assign
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const rawEvent = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        let eventName = "message";
        const dataLines: string[] = [];
        for (const line of rawEvent.split("\n")) {
          if (line.startsWith(":")) continue;
          if (line.startsWith("event:")) {
            eventName = line.slice(6).trim();
          } else if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trimStart());
          }
        }
        if (dataLines.length) {
          dispatch(eventName, dataLines.join("\n"));
        }
      }
    };

    const open = async () => {
      if (cancelled) return;
      const token = getToken();
      try {
        const res = await fetch(`${API_BASE}/support/stream`, {
          method: "GET",
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok || !res.body) {
          throw new Error(`SSE open failed: HTTP ${res.status}`);
        }
        reader = res.body.getReader();
        try { onConnect(); } catch { /* noop */ }
        // eslint-disable-next-line no-constant-condition
        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) parseChunk(decoder.decode(value, { stream: true }));
        }
      } catch {
        try { onDisconnect?.(); } catch { /* noop */ }
      } finally {
        if (reader) {
          try { reader.releaseLock(); } catch { /* noop */ }
          reader = null;
        }
      }
      if (!cancelled) {
        reconnectTimer = setTimeout(open, 5000);
      }
    };

    void open();

    return () => {
      cancelled = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (reader) {
        try { reader.cancel().catch(() => undefined); } catch { /* noop */ }
        try { reader.releaseLock(); } catch { /* noop */ }
        reader = null;
      }
    };
  },
};
